import { useState, useEffect, useRef, useCallback } from 'react';

// The Fullscreen API, with the webkit-prefixed fallback older Safari (macOS,
// iPadOS) still needs. iPhone Safari exposes neither on non-video elements, so
// there the hook stays in its in-page "fill the browser" mode.
const fullscreenElement = () =>
    document.fullscreenElement || document.webkitFullscreenElement || null;

const requestRealFullscreen = () => {
    const el = document.documentElement;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!request) return;
    try {
        const result = request.call(el);
        // Standard API returns a promise; it rejects if the browser refuses
        // (no user gesture, permissions policy, ...). The in-page mode still
        // applies, so there is nothing to do beyond not leaking the rejection.
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (e) {
        // Older prefixed implementations can throw instead.
    }
};

const exitRealFullscreen = () => {
    if (!fullscreenElement()) return;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (!exit) return;
    try {
        const result = exit.call(document);
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (e) {
        // Already leaving fullscreen, or the document is no longer active.
    }
};

export default function useFullscreen() {
    const [fullscreen, setFullscreenState] = useState(false);
    const historyPushedRef = useRef(false);
    const programmaticBackRef = useRef(false);
    // True once the browser has actually entered real fullscreen for us, so a
    // fullscreenchange from something else (e.g. a video's own fullscreen
    // while we're only filling the browser) doesn't end our mode.
    const realFullscreenRef = useRef(false);

    const enterFullscreen = useCallback(() => {
        window.history.pushState({ fullscreen: true }, '');
        historyPushedRef.current = true;
        setFullscreenState(true);
        // Must run synchronously inside the click handler - browsers only
        // grant fullscreen from a user gesture.
        requestRealFullscreen();
    }, []);

    const exitFullscreen = useCallback(() => {
        setFullscreenState(false);
        realFullscreenRef.current = false;
        exitRealFullscreen();
        if (historyPushedRef.current) {
            historyPushedRef.current = false;
            programmaticBackRef.current = true;
            window.history.back();
        }
    }, []);

    const toggleFullscreen = useCallback(() => {
        if (historyPushedRef.current) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    }, [enterFullscreen, exitFullscreen]);

    useEffect(() => {
        const handlePopstate = () => {
            if (programmaticBackRef.current) {
                programmaticBackRef.current = false;
                return;
            }
            if (historyPushedRef.current) {
                // Back button already consumed our history entry; just leave
                // real fullscreen too.
                historyPushedRef.current = false;
                realFullscreenRef.current = false;
                setFullscreenState(false);
                exitRealFullscreen();
            }
        };

        const handleKeydown = (e) => {
            // Only reaches us in the in-page mode: in real fullscreen the
            // browser consumes Escape and fires fullscreenchange instead.
            if (e.key === 'Escape' && historyPushedRef.current) {
                exitFullscreen();
            }
        };

        const handleFullscreenChange = () => {
            const el = fullscreenElement();
            if (el) {
                if (el === document.documentElement && historyPushedRef.current) {
                    realFullscreenRef.current = true;
                }
                return;
            }
            // The user left real fullscreen through the browser (Esc, F11,
            // the browser's own exit UI) - drop the in-page mode and the
            // history entry along with it.
            if (realFullscreenRef.current) {
                realFullscreenRef.current = false;
                if (historyPushedRef.current) exitFullscreen();
            }
        };

        window.addEventListener('popstate', handlePopstate);
        window.addEventListener('keydown', handleKeydown);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        return () => {
            window.removeEventListener('popstate', handlePopstate);
            window.removeEventListener('keydown', handleKeydown);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, [exitFullscreen]);

    // Leaving the room while in real fullscreen shouldn't strand the user in
    // fullscreen on whatever page comes next.
    useEffect(() => () => {
        if (realFullscreenRef.current) {
            realFullscreenRef.current = false;
            exitRealFullscreen();
        }
    }, []);

    return { fullscreen, enterFullscreen, exitFullscreen, toggleFullscreen };
}
